import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDateISO, formatTimePretty } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { CheckCircle, ArrowLeft, UserCircle, DoorOpen, PhoneCall, Clock } from "@phosphor-icons/react";

// Kiosk runs in fullscreen, public (no auth)
export default function Kiosk() {
    const [screen, setScreen] = useState("welcome"); // welcome | checkin | walkin | done
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000 * 30);
        return () => clearInterval(t);
    }, []);

    return (
        <div
            className="min-h-screen relative bg-bone"
            style={{
                backgroundImage:
                    "linear-gradient(rgba(249,249,246,0.85), rgba(249,249,246,0.9)), url('https://images.pexels.com/photos/4709466/pexels-photo-4709466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <header className="px-8 md:px-12 py-6 flex items-center justify-between border-b border-divider/60">
                <div className="flex items-center gap-4">
                    <Logo size={48} className="text-moss" />
                    <div>
                        <div className="font-serif text-2xl">Noble Accounting Services</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ash">A Zakir Dema Company · In-house Check-in</div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-mono text-lg">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ash">{now.toDateString()}</div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-8 py-12 md:py-20">
                {screen === "welcome" && <Welcome onCheckin={() => setScreen("checkin")} onWalkin={() => setScreen("walkin")} />}
                {screen === "checkin" && <CheckIn onBack={() => setScreen("welcome")} onDone={() => setScreen("done")} />}
                {screen === "walkin" && <WalkIn onBack={() => setScreen("welcome")} onDone={() => setScreen("done")} />}
                {screen === "done" && <Done onReset={() => setScreen("welcome")} />}
            </main>

            <footer className="absolute bottom-4 left-0 right-0 px-8 text-xs uppercase tracking-[0.25em] text-ash flex justify-between">
                <span>Secure check-in</span>
                <Link to="/" className="hover:text-ink" data-testid="kiosk-exit">Exit</Link>
            </footer>
        </div>
    );
}

function Welcome({ onCheckin, onWalkin }) {
    return (
        <div className="animate-fade-up" data-testid="kiosk-welcome">
            <p className="text-xs uppercase tracking-[0.25em] text-ash mb-4">Welcome</p>
            <h1 className="font-serif text-5xl md:text-7xl font-light mb-4">Please sign in.</h1>
            <p className="text-lg text-ash mb-12 max-w-2xl">Tap the option that describes your visit today.</p>
            <div className="grid md:grid-cols-2 gap-8">
                <button
                    onClick={onCheckin}
                    className="group relative p-10 md:p-14 text-left border border-divider bg-bone hover:border-moss hover:bg-stone/40 transition-all duration-200"
                    data-testid="kiosk-option-checkin"
                >
                    <UserCircle size={48} weight="light" className="text-moss mb-8" />
                    <div className="font-serif text-3xl md:text-4xl mb-3">I have an appointment</div>
                    <p className="text-ash text-sm md:text-base">Sign in with your phone number to let us know you've arrived.</p>
                    <div className="mt-8 text-xs uppercase tracking-[0.25em] text-moss">Begin →</div>
                </button>
                <button
                    onClick={onWalkin}
                    className="group relative p-10 md:p-14 text-left border border-divider bg-bone hover:border-terracotta hover:bg-stone/40 transition-all duration-200"
                    data-testid="kiosk-option-walkin"
                >
                    <DoorOpen size={48} weight="light" className="text-terracotta mb-8" />
                    <div className="font-serif text-3xl md:text-4xl mb-3">I'm a walk-in</div>
                    <p className="text-ash text-sm md:text-base">Book the next available 30-minute slot today.</p>
                    <div className="mt-8 text-xs uppercase tracking-[0.25em] text-terracotta">Begin →</div>
                </button>
            </div>
        </div>
    );
}

function CheckIn({ onBack, onDone }) {
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        if (phone.trim().length < 4) {
            toast.error("Enter your phone number");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/kiosk/checkin", { phone: phone.trim() });
            setResult(data);
            toast.success("You're checked in");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No booking found");
        } finally {
            setLoading(false);
        }
    };

    if (result) {
        return (
            <div className="animate-fade-up text-center" data-testid="kiosk-checkin-success">
                <CheckCircle size={96} weight="light" className="text-moss mx-auto mb-6" />
                <h1 className="font-serif text-5xl md:text-6xl font-light mb-4">Welcome, {result.customer_name}.</h1>
                <p className="text-ash text-lg mb-8">
                    Your {result.service_name} appointment at {formatTimePretty(result.appointment_time)} is confirmed.
                    Please have a seat.
                </p>
                <button onClick={onDone} className="bg-moss text-bone px-10 py-5 text-sm uppercase tracking-[0.25em] hover:bg-moss-hover" data-testid="kiosk-checkin-done">
                    Done
                </button>
            </div>
        );
    }

    return (
        <div className="animate-fade-up max-w-xl" data-testid="kiosk-checkin">
            <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-ash hover:text-ink mb-6" data-testid="kiosk-checkin-back">
                <ArrowLeft size={16} /> Back
            </button>
            <h1 className="font-serif text-5xl font-light mb-3">Enter your phone.</h1>
            <p className="text-ash mb-10">The number used when booking.</p>
            <label className="block">
                <div className="text-xs uppercase tracking-[0.25em] text-ash mb-3 flex items-center gap-2">
                    <PhoneCall size={14} /> Phone number
                </div>
                <input
                    autoFocus
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    className="w-full bg-bone border border-divider rounded-none px-6 py-6 text-2xl font-mono focus:outline-none focus:border-moss focus:ring-1 focus:ring-moss"
                    data-testid="kiosk-checkin-phone"
                />
            </label>
            <button onClick={submit} disabled={loading} className="mt-8 w-full bg-moss text-bone py-6 text-sm uppercase tracking-[0.25em] hover:bg-moss-hover disabled:opacity-40" data-testid="kiosk-checkin-submit">
                {loading ? "Checking…" : "Check me in"}
            </button>
        </div>
    );
}

function WalkIn({ onBack, onDone }) {
    const [services, setServices] = useState([]);
    const [slots, setSlots] = useState([]);
    const [form, setForm] = useState({ name: "", phone: "", service_id: "", time: "" });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const today = formatDateISO(new Date());

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data));
        api.get(`/slots/available?date=${today}`).then((r) => setSlots(r.data.slots || []));
    }, [today]);

    const submit = async () => {
        if (!form.name.trim() || !form.phone.trim() || !form.service_id || !form.time) {
            toast.error("Fill every field");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/kiosk/walkin", {
                customer_name: form.name,
                customer_phone: form.phone,
                service_id: form.service_id,
                appointment_time: form.time,
            });
            setResult(data);
            toast.success("Walk-in registered");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not register");
        } finally {
            setLoading(false);
        }
    };

    if (result) {
        return (
            <div className="animate-fade-up text-center" data-testid="kiosk-walkin-success">
                <CheckCircle size={96} weight="light" className="text-terracotta mx-auto mb-6" />
                <h1 className="font-serif text-5xl md:text-6xl font-light mb-4">You're on the list.</h1>
                <p className="text-ash text-lg mb-8">
                    {result.customer_name} — {result.service_name} at {formatTimePretty(result.appointment_time)}. Have a seat.
                </p>
                <button onClick={onDone} className="bg-terracotta text-white px-10 py-5 text-sm uppercase tracking-[0.25em] hover:bg-terracotta-hover" data-testid="kiosk-walkin-done">
                    Done
                </button>
            </div>
        );
    }

    return (
        <div className="animate-fade-up max-w-3xl" data-testid="kiosk-walkin">
            <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-ash hover:text-ink mb-6" data-testid="kiosk-walkin-back">
                <ArrowLeft size={16} /> Back
            </button>
            <h1 className="font-serif text-5xl font-light mb-3">Walk-in sign in.</h1>
            <p className="text-ash mb-10">Pick a service and an open slot remaining today.</p>

            <div className="space-y-8">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-ash mb-3">Service</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {services.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => setForm({ ...form, service_id: s.id })}
                                className={`border px-4 py-5 text-sm transition-colors ${
                                    form.service_id === s.id ? "bg-moss text-bone border-moss" : "bg-bone border-divider hover:border-moss"
                                }`}
                                data-testid={`kiosk-walkin-service-${s.id}`}
                            >
                                {s.name}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-ash mb-3 flex items-center gap-2">
                        <Clock size={14} /> Available slots today
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="kiosk-walkin-slots">
                        {slots.map((s) => (
                            <button
                                key={s.time}
                                disabled={!s.available}
                                onClick={() => setForm({ ...form, time: s.time })}
                                className={`slot-btn py-4 font-mono text-base border ${
                                    form.time === s.time
                                        ? "bg-moss text-bone border-moss"
                                        : s.available
                                        ? "bg-bone border-divider"
                                        : "bg-stone border-divider text-ash line-through cursor-not-allowed"
                                }`}
                                data-testid={`kiosk-walkin-slot-${s.time}`}
                            >
                                {formatTimePretty(s.time)}
                            </button>
                        ))}
                    </div>
                    {slots.every((s) => !s.available) && (
                        <div className="mt-4 text-sm text-ash border border-dashed border-divider p-4">
                            No open slots remaining today.
                        </div>
                    )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <label className="block">
                        <div className="text-xs uppercase tracking-[0.25em] text-ash mb-2">Your name</div>
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-bone border border-divider rounded-none px-5 py-4 text-lg focus:outline-none focus:border-moss"
                            data-testid="kiosk-walkin-name"
                        />
                    </label>
                    <label className="block">
                        <div className="text-xs uppercase tracking-[0.25em] text-ash mb-2">Phone</div>
                        <input
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="w-full bg-bone border border-divider rounded-none px-5 py-4 text-lg font-mono focus:outline-none focus:border-moss"
                            data-testid="kiosk-walkin-phone"
                        />
                    </label>
                </div>

                <button onClick={submit} disabled={loading} className="w-full bg-terracotta text-white py-6 text-sm uppercase tracking-[0.25em] hover:bg-terracotta-hover disabled:opacity-40" data-testid="kiosk-walkin-submit">
                    {loading ? "Registering…" : "Check in as walk-in"}
                </button>
            </div>
        </div>
    );
}

function Done({ onReset }) {
    useEffect(() => {
        const t = setTimeout(onReset, 5000);
        return () => clearTimeout(t);
    }, [onReset]);
    return (
        <div className="animate-fade-up text-center py-20" data-testid="kiosk-done">
            <h1 className="font-serif text-5xl md:text-6xl font-light mb-4">Thank you.</h1>
            <p className="text-ash">Returning to welcome screen…</p>
        </div>
    );
}
